/**
 * attributeValidation.js
 *
 * 系统全局 EAV 属性验证工具
 * 统一前端属性验证逻辑，供 ProductForm / ChannelEavForm / SupplierForm / EmployeeForm 等所有使用 EAV 属性的表单调用。
 *
 * 后端对应逻辑：EavService::validateEavAttributes()
 *   位置：module/Core/src/Service/EavService.php:446
 *
 * 验证内容：
 *   1. is_required=1 的属性是否填写
 *   2. input_validation（或 input_type 隐式推断）的格式验证
 */

import React from 'react';

/**
 * 获取属性的有效验证类型
 * 优先使用 input_validation（非 None），否则根据 input_type 隐式推断
 *
 * @param {Object} attr  属性配置对象（含 input_validation, input_type）
 * @returns {string}      验证类型名称，如 'Decimal Number', 'None'
 */
export const getEffectiveValidation = (attr) => {
  if (attr.input_validation && attr.input_validation !== 'None') return attr.input_validation;
  const implicitMap = {
    'Price': 'Decimal Number',
    'URL': 'URL',
    'Date': 'Date',
    'Date and Time': 'Date and Time',
  };
  return implicitMap[attr.input_type] || 'None';
};

/**
 * 验证单个属性值，返回错误提示字符串或空字符串
 *
 * @param {Object} attr          属性配置（含 attribute_code, default_label, is_required, input_type, input_validation）
 * @param {*}      value         属性值
 * @param {Function} t           翻译函数（可选，不传返回英文硬编码提示）
 * @param {Function} getLabel    获取显示标签的函数（可选，默认取 default_label || attribute_code）
 * @returns {string}             '')      验证通过
 *                               非空字符串  错误提示
 */
export const validateSingleValue = (attr, value, t, getLabel) => {
  if (!attr) return '';

  const code = attr.attribute_code || '';
  const label = getLabel ? getLabel(attr) : (attr.default_label || code || '');
  const sv = String(value || '').trim();
  const isEmpty = value === undefined || value === null || sv === '';

  // 1. 必填验证
  if (attr.is_required && attr.is_required !== '0' && isEmpty) {
    return t
      ? t('validation.attribute_required', { label })
      : `Attribute "${label}" is required`;
  }

  // 非必填且无值，跳过后续格式验证
  if (isEmpty) return '';

  // 2. 输入验证规则
  const validation = getEffectiveValidation(attr);
  if (validation === 'None') return '';

  // URL 类型：可能是 JSON 数组字符串，验证每个元素
  if (validation === 'URL') {
    let urls = [];
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      if (Array.isArray(parsed)) urls = parsed;
      else urls = [String(value)];
    } catch (e) {
      urls = [String(value)];
    }
    for (let i = 0; i < urls.length; i++) {
      const u = String(urls[i] || '').trim();
      if (u === '') continue;
      if (!/^https?:\/\/.+\..+/i.test(u)) {
        return t
          ? t('validation.attribute_url_invalid', { label, n: i + 1 })
          : `Attribute "${label}" link #${i + 1} is invalid`;
      }
    }
    return '';
  }

  // 其他验证类型
  switch (validation) {
    case 'Decimal Number':
      if (!/^-?\d*\.?\d+$/.test(sv)) {
        return t ? t('validation.must_be_decimal') : 'Must be a decimal number';
      }
      break;
    case 'Integer Number':
      if (!/^-?\d+$/.test(sv)) {
        return t ? t('validation.must_be_integer') : 'Must be an integer';
      }
      break;
    case 'Email':
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sv)) {
        return t ? t('validation.must_be_email') : 'Must be a valid email';
      }
      break;
    case 'Letters':
      if (!/^[A-Za-z]+$/.test(sv)) {
        return t ? t('validation.must_be_letters') : 'Must contain only letters';
      }
      break;
    case 'Letters or Numbers':
      if (!/^[A-Za-z0-9]+$/.test(sv)) {
        return t ? t('validation.must_be_letters_numbers') : 'Must contain only letters or numbers';
      }
      break;
    case 'Date':
      if (!/^\d{4}-\d{2}-\d{2}$/.test(sv)) {
        return t ? t('validation.must_be_date') : 'Must be YYYY-MM-DD';
      }
      break;
    case 'Date and Time':
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(sv)) {
        return t ? t('validation.must_be_datetime') : 'Must be YYYY-MM-DDTHH:MM';
      }
      break;
  }

  return '';
};

/**
 * 验证整个属性配置集的所有值
 *
 * @param {Object[]} eavConfig        属性配置数组
 * @param {Object}   attributes       属性值对象 { code: value }
 * @param {Function} t                翻译函数
 * @param {Object}   [options]        选项
 * @param {string[]} [options.excludeCodes]  跳过验证的属性代码列表
 * @param {Function} [options.getLabel]      自定义取标签函数 (attr) => string
 * @returns {{ valid: boolean, errors: string[] }}
 */
export const validateAllEavAttributes = (eavConfig, attributes, t, options = {}) => {
  const { excludeCodes = [], getLabel } = options;
  const errors = [];

  for (const attr of eavConfig) {
    const code = attr.attribute_code || '';
    if (excludeCodes.includes(code)) continue;
    const value = attributes[code];
    const error = validateSingleValue(attr, value, t, getLabel);
    if (error) errors.push(error);
  }

  return { valid: errors.length === 0, errors };
};

/**
 * 快捷函数：验证并返回第一条错误或 null
 * 适用于 Controller/Form 场景：需要在第一条错误时立即停止
 *
 * @param {Object[]} eavConfig        属性配置数组
 * @param {Object}   attributes       属性值对象 { code: value }
 * @param {Function} t                翻译函数
 * @param {Object}   [options]        选项（同上）
 * @returns {string|null}  第一条错误提示，无错误返回 null
 */
export const findFirstEavError = (eavConfig, attributes, t, options = {}) => {
  const result = validateAllEavAttributes(eavConfig, attributes, t, options);
  return result.valid ? null : result.errors[0];
};

/**
 * 判断属性是否必填（兼容 int 1 / string '1' / boolean true）
 *
 * 所有 EAV 表单必须使用此函数取代手写星号逻辑。
 * 保证跨表单、跨实例（m2_1_ / m2_2_ / m2_3_）的必填检查表现一致。
 *
 * @param {Object|string|number|boolean} attr  属性配置对象，或 is_required 原始值
 * @returns {boolean}
 */
export const isAttributeRequired = (attr) => {
  const val = typeof attr === 'object' && attr !== null ? attr.is_required : attr;
  return [1, '1', true].includes(val);
};

/**
 * 统一渲染必填红色星号。
 *
 * 用法：
 *   {renderAsterisk(attr)}
 *   {renderAsterisk(attr.is_required)}
 *
 * @param {Object|string|number|boolean} isRequired  属性对象或 is_required 值
 * @returns {JSX.Element|null}
 */
export const renderAsterisk = (isRequired) => {
  return isAttributeRequired(isRequired)
    ? React.createElement('span', { className: 'text-red-500 ml-1' }, '*')
    : null;
};
